// ============================================================================
// ui/tooltip.js — the app's own hover bubble, shared
// ============================================================================
// Lifted out of `ui/config-adder.js` on 2026-08-10, unchanged in behaviour, when
// the Size ladder needed the same thing. Everything below was written for the ±
// buttons and every word of it applies to any control that has to explain itself.
//
// ── THE SITE'S OWN TOOLTIP, NOT THE BROWSER'S (Ryan 2026-08-09) ─────────────
// `title` is unstyleable, opens after an OS delay of roughly a second, truncates
// where it likes and cannot wrap — and this copy is now full sentences. The
// recipe is the theme's `.header-icon-tooltip` (`assets/custom-quote.css`):
// #000F1C on a 1px white rim, 6px radius, 12px/500, and the two-triangle arrow
// that leaves ~1px of the outline showing. Ported rather than approximated, so
// the tool's tooltip and the storefront's are the same object to a buyer who has
// just come from the header.
// ⛔ A HOST MUST NOT ALSO SET `title`. Leaving one shows the native bubble a
// second after this one, saying the same thing in two visual languages. Put the
// copy in `aria-label` instead — this bubble is `aria-hidden` decoration.
//
// ── IT LIVES ON <body>, NOT INSIDE THE CONTROL ──────────────────────────────
// The first cut put the bubble inside the button and failed in both the ways he
// described: *"the +/- buttons have been pushed down half way off alignment"* and
// *"the tooltip doesn't overlap everything… so it mostly underlaps stuff and
// becomes unusable."*
//
// ⛔ Z-INDEX CANNOT BEAT CLIPPING. However high the bubble was stacked it was a
// descendant of the pane's scroller and of the row, so their `overflow` clipped
// it long before stacking order was ever consulted — the same trap the flying
// ghost hit, and the same fix. A `position: fixed` element parented to <body> has
// no clipping ancestor.
// ⚠ And a child inside the button is a child that can move the icon: the button
// flex-centres its children, so even a sometimes-displayed node belongs outside.
//
// ONE bubble for the whole page — a buyer hovers one control at a time, and a
// node per control was twenty elements standing by to do nothing.
//
// ⚠ THE CLASS IS STILL `.wd-cfg-tip`. It is named after the surface that first
// needed it, which now reads oddly, but the name is load-bearing in ~10 CSS rules
// (the arrow triangles, the flip-below variants) and renaming it buys nothing a
// comment cannot.
// ============================================================================

import { isPhone } from './mobile-launch.js';

let tipEl = null;
function bubble() {
  if (tipEl && tipEl.isConnected) return tipEl;
  tipEl = document.createElement('div');
  tipEl.className = 'wd-cfg-tip';
  tipEl.setAttribute('aria-hidden', 'true');   // the control's aria-label carries the accessible copy
  document.body.appendChild(tipEl);
  return tipEl;
}

/**
 * Show the bubble against `el`, reading its copy from `el._wdTip`.
 *
 * ⭐ `_wdTip` MAY BE A FUNCTION, and for anything whose wording depends on live state it must be.
 * The Size ladder's copy names the rung you are currently standing on ("change your 45lb plate to
 * 55lb"), and that rung moves on a click the tile handles OPTIMISTICALLY — it repaints its own row
 * before the panel rebuilds. A string captured at build time would then describe the plate you just
 * left. Resolved at show time, it cannot be stale.
 *
 * `el._wdTipTone` picks a variant — `'warn'` for the reddish "already assigned" bubble.
 */
export function showTip(el) {
  /* ⛔ NEVER ON A PHONE (Ryan 2026-08-10) ────────────────────────────────────────────────────────
   * *"they exist too on mobile, but they shouldn't. when you press and hold on mobile the tooltip
   * appears, but no one will know this, and they also use this section of the screen to press and
   * swipe to go between models — but if their finger just so happens to press and attempt to swipe
   * on the + or −, they'll see the tooltip appear. so no tooltips on mobile."*
   *
   * ⭐ A HOVER AFFORDANCE HAS NO TOUCH EQUIVALENT, and pretending otherwise is what produced this.
   * `pointerenter` fires on touch-DOWN, so on a phone every press — including the first millimetre
   * of a swipe — opens a bubble explaining a control the buyer is not trying to read about. It is
   * undiscoverable (nothing says "hold me"), it is unrequested, and it lands on the one strip of
   * the card people use to page between products.
   * ⚠ Guarded HERE rather than by skipping the listeners at construction, because this is the one
   * place both routes in (`pointerenter` and keyboard `focus`) already meet, and asking at event
   * time means a resize across the breakpoint is right without anything having to be rebuilt. */
  if (isPhone()) return;
  const raw = el._wdTip;
  const text = typeof raw === 'function' ? raw() : raw;
  if (!text) return;
  const box = bubble();
  box.textContent = text;
  box.classList.toggle('wd-cfg-tip--warn', el._wdTipTone === 'warn');
  box.classList.add('is-on');
  const b = el.getBoundingClientRect();
  const r = box.getBoundingClientRect();
  const GAP = 10;
  /* ⚠ CLAMPED TO THE VIEWPORT. These controls sit at the ENDS of a pill or the edge of a 5-column
     grid, so a 240px bubble centred on one that is 12px from the screen edge would hang off it. */
  const half = r.width / 2;
  const cx = Math.min(Math.max(b.left + b.width / 2, 8 + half), window.innerWidth - 8 - half);
  const below = b.top - r.height - GAP < 8;    // no room above → flip under, arrow turns with it
  box.classList.toggle('is-below', below);
  box.style.left = `${Math.round(cx - half)}px`;
  box.style.top = `${Math.round(below ? b.bottom + GAP : b.top - r.height - GAP)}px`;
  /* The arrow's X comes from the CONTROL's centre, not the bubble's, so it still points at the
     control after the clamp above has slid the bubble sideways. */
  box.style.setProperty('--tip-arrow-x', `${Math.round(b.left + b.width / 2 - (cx - half))}px`);
}

export function hideTip() { if (tipEl) tipEl.classList.remove('is-on'); }

/**
 * Wire one control to the shared bubble.
 *
 * ⚠ Hover AND keyboard focus, and hidden again on click — because after a press the thing the copy
 * described has usually changed, and on a touch device a tap leaves a phantom hover that would
 * otherwise park a bubble on screen with no way to dismiss it.
 *
 * @param {HTMLElement} el
 * @param {string|(() => string)} text  a function whenever the wording depends on live state
 * @param {{tone?: 'warn'}} [opts]
 */
export function attachTip(el, text, opts = {}) {
  el._wdTip = text;
  el._wdTipTone = opts.tone || null;
  el.addEventListener('pointerenter', () => showTip(el));
  el.addEventListener('pointerleave', hideTip);
  el.addEventListener('focus', () => { if (el.matches(':focus-visible')) showTip(el); });
  el.addEventListener('blur', hideTip);
  el.addEventListener('click', hideTip);
}
